import React, { useEffect } from 'react'
import Snackbar from '@mui/material/Snackbar'
import { CheckCircle, CircleNotch, Info, Warning, X, XCircle } from '@phosphor-icons/react'

const TOAST_VARIANTS = {
  loading: {
    title: 'Preparando archivo…',
    Icon: CircleNotch,
    iconBackground: '#E8F1FB',
    progress: '#27588D'
  },
  info: { title: 'Información', Icon: Info, iconBackground: '#F1F3F6', progress: '#597FA9' },
  success: {
    title: 'Acción completada',
    Icon: CheckCircle,
    iconBackground: '#E8F7EE',
    progress: '#168F56'
  },
  warning: { title: 'Atención', Icon: Warning, iconBackground: '#FFF4DB', progress: '#B7791F' },
  error: {
    title: 'Ocurrió un error',
    Icon: XCircle,
    iconBackground: '#FDECEC',
    progress: '#D14343'
  }
}

export const UBIQUA_TOWER_TOAST_DEFAULT_DURATION_MS = 3800

/**
 * Aviso flotante reutilizable para mostrar el estado de una acción en cualquier módulo.
 * `severity` acepta loading, info, success, warning o error y define el icono y los colores.
 * `message` contiene el mensaje; `text` es una alternativa compatible.
 * `title`, `icon` y `colors` permiten personalizar la presentación.
 *
 * Se oculta si open es false o no hay mensaje. `onClose` debe actualizar el estado
 * del componente padre para cerrarlo. Por defecto dura 3800 ms; usa durationMs={null}
 * y showCountdown={false} para mantenerlo visible, por ejemplo durante una descarga.
 * `filename` muestra un nombre de archivo y `action` acepta { label, onClick }
 * para ofrecer una acción opcional, como reintentar. Cambia `id` para un aviso nuevo.
 *
 * Ejemplo:
 * <UbiquaTowerToastAlert
 *   open={Boolean(alert)}
 *   id={alert?.id}
 *   severity="success"
 *   title="Acción completada"
 *   message="Los cambios se guardaron correctamente."
 *   icon={<CheckCircle size={18} weight="regular" aria-hidden="true" />}
 *   colors={{ iconBackground: '#E8F7EE', progress: '#168F56' }}
 *   onClose={() => setAlert(null)}
 * />
 */
export default function UbiquaTowerToastAlert({
  id,
  open = true,
  severity = 'success',
  title,
  message,
  text,
  filename,
  action,
  icon,
  colors = {},
  onClose,
  durationMs = UBIQUA_TOWER_TOAST_DEFAULT_DURATION_MS,
  showCountdown = true,
  anchorOrigin = { vertical: 'top', horizontal: 'right' },
  className = '',
  snackbarClassName = '',
  snackbarSx = {}
}) {
  const bodyText = message ?? text
  const variant = TOAST_VARIANTS[severity] || TOAST_VARIANTS.error
  const VariantIcon = variant.Icon
  const resolvedColors = { ...variant, ...colors }

  useEffect(() => {
    if (!open || !bodyText || durationMs == null || typeof onClose !== 'function') return undefined

    const timeoutId = window.setTimeout(() => {
      onClose()
    }, durationMs)

    return () => window.clearTimeout(timeoutId)
  }, [bodyText, durationMs, id, onClose, open])

  if (!open || !bodyText) return null

  return (
    <Snackbar
      open={open}
      key={id}
      className={['ubiqua-tower-toast-snackbar', snackbarClassName].filter(Boolean).join(' ')}
      onClose={(_, reason) => {
        if (reason === 'clickaway') return
        onClose?.()
      }}
      anchorOrigin={anchorOrigin}
      autoHideDuration={durationMs}
      sx={{
        left: 'auto',
        width: 'auto',
        maxWidth: 'calc(100vw - 32px)',
        ...snackbarSx
      }}
    >
      <div
        id={id}
        role={severity === 'error' ? 'alert' : 'status'}
        aria-live="polite"
        className={['ubiqua-tower-toast', className].filter(Boolean).join(' ')}
        style={{
          '--ubiqua-tower-toast-icon-bg': resolvedColors.iconBackground,
          '--ubiqua-tower-toast-progress': resolvedColors.progress,
          '--ubiqua-tower-toast-duration': `${durationMs}ms`
        }}
      >
        <div className="ubiqua-tower-toast__icon">
          {icon ?? (
            <VariantIcon
              className={severity === 'loading' ? 'ubiqua-motion-spin' : undefined}
              size={20}
              weight="regular"
              color={resolvedColors.progress}
              aria-hidden="true"
            />
          )}
        </div>
        <div className="ubiqua-tower-toast__body">
          {(title ?? variant.title) ? (
            <strong className="ubiqua-tower-toast__title">{title ?? variant.title}</strong>
          ) : null}
          <span className="ubiqua-tower-toast__message">{bodyText}</span>
          {filename && <small className="ubiqua-tower-toast__filename">{filename}</small>}
          {action && (
            <button type="button" className="ubiqua-tower-toast__action" onClick={action.onClick}>
              {action.label}
            </button>
          )}
        </div>
        {typeof onClose === 'function' ? (
          <button
            type="button"
            className="ubiqua-tower-toast__close"
            aria-label="Cerrar alerta"
            onClick={onClose}
          >
            <X size={16} weight="bold" aria-hidden="true" />
          </button>
        ) : null}
        {showCountdown ? (
          <span className="ubiqua-tower-toast__countdown" aria-hidden="true" />
        ) : null}
      </div>
    </Snackbar>
  )
}
